/**
 * Block Loader Service — Types + Catalog + Lazy Loader
 *
 * Provides typed access to the block catalog (catalog-v2.json),
 * type definitions for loaded block data, and lazy-loading of
 * individual block 6-file structures from blocks-v2/.
 */

import catalogJson from '@cogo/schema/catalog'
import { applianceHidesCommerceMarker } from '@/services/appliance-commerce-surface'

// ---------------------------------------------------------------------------
// Catalog types
// ---------------------------------------------------------------------------

export interface CatalogBlock {
  id: string
  category: string
  description: string
  rendererTypes: string[]
  composability: {
    position: string
    pairs_with: string[]
    standalone: boolean
  }
  tags: string[]
  complexity: string
}

export interface CatalogCategory {
  id: string
  label: string
  description: string
  blocks: CatalogBlock[]
}

export interface BlockCatalog {
  version: string
  totalBlocks: number
  categories: CatalogCategory[]
}

// ---------------------------------------------------------------------------
// Loaded block types (used by loadBlock in Task 2)
// ---------------------------------------------------------------------------

export interface LoadedBlock {
  manifest: Record<string, unknown> // block.cogo.json
  ui: Record<string, unknown> // ui.cogo.json
  flow: Record<string, unknown> // flow.cogo.json
  variables: Record<string, unknown> // variables.cogo.json
  events: Record<string, unknown> // events.cogo.json
  datasource: Record<string, unknown> // datasource.cogo.json
}

// ---------------------------------------------------------------------------
// Catalog accessor
// ---------------------------------------------------------------------------

/**
 * Returns the block catalog typed as BlockCatalog.
 *
 * The catalog is statically imported from @cogo/schema so it is
 * tree-shaken and bundled at build time — no runtime fetch needed.
 *
 * Under an appliance distribution the `ecommerce` category is withheld: its
 * 11 blocks are `cart-sidebar`, `checkout-form`, `order-summary`,
 * `product-card`, `product-detail`, `wishlist`, … — a shopping cart offered
 * in the block palette (features/project/components/block-catalog-panel.tsx
 * and features/page-composer/components/community-block-panel.tsx) is
 * commerce reaching the user just as surely as a `/studio/commerce` route
 * would, and no route filter was ever going to see it. The category id is
 * the SAME `ecommerce` token the template catalogue and the featured gallery
 * declare, so it is withheld by the same shared marker + predicate.
 *
 * `pricing` (4 blocks) is deliberately NOT withheld: a price/fee table is
 * generic page furniture, not a transaction, and an appliance customer has
 * legitimate reason to lay out a fee schedule. Withholding it would be
 * removal by word association rather than by declared kind.
 */
export function getCatalog(): BlockCatalog {
  const catalog = catalogJson as unknown as BlockCatalog
  const categories = catalog.categories.filter(
    (category) => !applianceHidesCommerceMarker(category.id),
  )
  if (categories.length === catalog.categories.length) return catalog
  return { ...catalog, categories }
}

// ---------------------------------------------------------------------------
// Block loader — lazy 6-file loader with caching
// ---------------------------------------------------------------------------

/** Cache of loaded blocks keyed by "category/blockId" */
const blockCache = new Map<string, LoadedBlock>()

/**
 * Returns true if the string is a valid path segment:
 * lowercase alphanumeric, hyphens, underscores; must start with alphanumeric.
 */
function isValidSegment(s: string): boolean {
  return /^[a-z0-9][a-z0-9_-]*$/.test(s)
}

/**
 * Base URL prefix for blocks-v2/ files.
 *
 * In Vite dev mode, served by the cogoBlocksPlugin in vite.config.ts.
 * In production builds, these files would be served from a static asset path.
 */
const BLOCKS_BASE_URL = '/cogo-blocks'

/**
 * Loads a single JSON file via fetch from the blocks-v2/ directory.
 * Returns an empty object if the file is missing or fails to parse.
 */
async function loadJsonFile(
  category: string,
  blockId: string,
  filename: string,
): Promise<Record<string, unknown>> {
  try {
    const url = `${BLOCKS_BASE_URL}/${category}/${blockId}/${filename}`
    const res = await fetch(url)
    if (!res.ok) {
      if (import.meta.env?.DEV) {
        console.debug(`[block-loader] ${category}/${blockId}/${filename} not found (${res.status})`)
      }
      return {}
    }
    return await res.json()
  } catch {
    if (import.meta.env?.DEV) {
      console.debug(`[block-loader] ${category}/${blockId}/${filename} fetch failed`)
    }
    return {}
  }
}

/**
 * Lazy-loads all 6 `.cogo.json` files for a block from blocks-v2/.
 *
 * Results are cached — calling loadBlock with the same category/blockId
 * returns the identical object reference.
 *
 * @param category  Block category directory (e.g. "hero", "pricing")
 * @param blockId   Block identifier (e.g. "hero-gradient", "pricing-simple")
 * @returns LoadedBlock with manifest, ui, flow, variables, events, datasource
 */
export async function loadBlock(
  category: string,
  blockId: string,
): Promise<LoadedBlock> {
  if (!isValidSegment(category) || !isValidSegment(blockId)) {
    throw new Error(`Invalid block path: ${category}/${blockId}`)
  }

  const cacheKey = `${category}/${blockId}`

  const cached = blockCache.get(cacheKey)
  if (cached) return cached

  const [manifest, ui, flow, variables, events, datasource] =
    await Promise.all([
      loadJsonFile(category, blockId, 'block.cogo.json'),
      loadJsonFile(category, blockId, 'ui.cogo.json'),
      loadJsonFile(category, blockId, 'flow.cogo.json'),
      loadJsonFile(category, blockId, 'variables.cogo.json'),
      loadJsonFile(category, blockId, 'events.cogo.json'),
      loadJsonFile(category, blockId, 'datasource.cogo.json'),
    ])

  const loaded: LoadedBlock = {
    manifest,
    ui,
    flow,
    variables,
    events,
    datasource,
  }

  blockCache.set(cacheKey, loaded)
  return loaded
}

/**
 * Clears the in-memory block cache. Useful for testing or
 * when blocks-v2 data may have changed.
 */
export function clearBlockCache(): void {
  blockCache.clear()
}
